/**
 * Objective — show view on the shared Inspector grammar (one-grammar law).
 * Overview leads with status / period / owner / project; the description renders
 * as markdown in a Body section; the domain-specific linked Key Results list
 * (each with its `ProgressBar`) is preserved INSIDE its own Inspector section
 * (with an "Add key result" aside action). Owner / project / timestamps sink to
 * a muted Technical tail; Audit closes the page.
 */
import {
  useShow,
  useList,
  useGo,
  useParsed,
  type HttpError,
} from "@refinedev/core";
import type { Objective, KeyResult } from "@aos/shared";
import { Icon } from "@/components/ui/icon";
import { Button } from "@/components/ui/button";
import {
  Inspector,
  InspectorHeader,
  InspectorSection,
  Field,
  FieldGrid,
  RefLink,
  AuditBlock,
  ShowPageSkeleton,
  ShowPageError,
  TechnicalId,
} from "@/components/inspector";
import { Markdown } from "@/components/app";
import { formatDateTime, humanize } from "@/lib/format";
import { keyResultProgress, ProgressBar } from "@/resources/key-results/progress";

/**
 * ObjectiveShow — objective detail with its linked Key Results and their
 * progress bars. Key results are fetched by `objective_id` equality filter.
 */
export function ObjectiveShow() {
  const go = useGo();
  const { id } = useParsed();
  const { query } = useShow<Objective, HttpError>({ resource: "objectives" });
  const { data, isLoading, isError, error, refetch } = query;
  const objective = data?.data;

  const { data: krData, isLoading: krLoading } = useList<KeyResult>({
    resource: "key-results",
    filters: [{ field: "objective_id", operator: "eq", value: id }],
    pagination: { pageSize: 200 },
    queryOptions: { enabled: id != null },
  });
  const keyResults = krData?.data ?? [];

  if (isLoading) return <ShowPageSkeleton />;
  if (isError || !objective) {
    return (
      <ShowPageError
        message={error?.message ?? "Objective not found."}
        onRetry={() => {
          void refetch();
        }}
      />
    );
  }

  return (
    <Inspector>
      <InspectorHeader
        title={objective.title}
        subtitle="An objective with its key results, linked metrics, and progress."
        status={objective.status}
        actions={
          <Button
            variant="outline"
            size="sm"
            onClick={() =>
              go({
                to: { resource: "objectives", action: "edit", id: objective.id },
              })
            }
          >
            <Icon name="edit" size={16} className="mr-2" /> Edit
          </Button>
        }
      />

      <InspectorSection title="Overview">
        <FieldGrid>
          <Field label="Status">{humanize(objective.status)}</Field>
          <Field label="Period">{objective.period}</Field>
          <Field label="Owner">
            <RefLink resource="users" id={objective.owner_user_id} />
          </Field>
          <Field label="Project">
            <RefLink resource="projects" id={objective.project_id} />
          </Field>
        </FieldGrid>
      </InspectorSection>

      {objective.description && objective.description.trim() ? (
        <InspectorSection title="Body">
          <Markdown source={objective.description} />
        </InspectorSection>
      ) : null}

      <InspectorSection
        title="Key Results"
        aside={
          <Button
            size="sm"
            variant="outline"
            onClick={() =>
              go({ to: { resource: "key-results", action: "create" } })
            }
          >
            Add key result
          </Button>
        }
      >
        {krLoading ? (
          <p className="text-sm text-muted-foreground">Loading key results…</p>
        ) : keyResults.length === 0 ? (
          <p className="text-sm text-muted-foreground">
            No key results linked yet.
          </p>
        ) : (
          <div className="space-y-4">
            {keyResults.map((kr) => {
              const pct = keyResultProgress(kr);
              return (
                <button
                  key={kr.id}
                  type="button"
                  className="block w-full rounded-md p-3 text-left hover:bg-accent"
                  onClick={() =>
                    go({
                      to: { resource: "key-results", action: "show", id: kr.id },
                    })
                  }
                >
                  <div className="mb-2 flex items-center justify-between gap-3">
                    <span className="font-medium">{kr.title}</span>
                    <span className="text-sm text-muted-foreground">
                      {kr.current_value ?? 0}
                      {kr.unit ? ` ${kr.unit}` : ""} /{" "}
                      {kr.target_value ?? "—"}
                      {kr.unit && kr.target_value != null ? ` ${kr.unit}` : ""}
                    </span>
                  </div>
                  <ProgressBar value={pct} />
                </button>
              );
            })}
          </div>
        )}
      </InspectorSection>

      <InspectorSection title="Technical">
        <FieldGrid className="text-muted-foreground">
          <Field label="Id">
            <TechnicalId id={objective.id} />
          </Field>
          <Field label="Owner">
            <RefLink resource="users" id={objective.owner_user_id} />
          </Field>
          <Field label="Project">
            <RefLink resource="projects" id={objective.project_id} />
          </Field>
          <Field label="Created">{formatDateTime(objective.created_at)}</Field>
          <Field label="Updated">{formatDateTime(objective.updated_at)}</Field>
        </FieldGrid>
      </InspectorSection>

      <InspectorSection title="Audit">
        <AuditBlock entityType="objectives" entityId={objective.id} />
      </InspectorSection>
    </Inspector>
  );
}

export default ObjectiveShow;
